import { HWS, MAX_ENTRIES, RESLINK, RLM, SCHEMA_VERSION } from './constants';
import { canExecuteHomepageRetargeting } from '../consent';
import { findDuplicateIndex } from './dedupe';
import { pickEvictionIndex } from './eviction';
import { isValidInput } from './inputValidation';
import {
  hasLocalStorage,
  isQuotaExceededError,
  readRetargetingCandidateStore,
  writeRetargetingCandidateStore,
} from './storage';
import type {
  RecordRetargetingCandidateOptions,
  RetargetingCandidate,
  RetargetingCandidateInput,
} from './retargeting.types';

function currentTimestamp(now: () => Date): string | null {
  try {
    return now().toISOString();
  } catch {
    return null;
  }
}

// Normalizes reslink marshaCode to array-at-rest. Assumes input passed isReslinkInput; array is shallow-copied to isolate from caller mutation.
function toMarshaCodeArray(input: string | string[]): string[] {
  return Array.isArray(input) ? input.slice() : [input];
}

function buildCandidate(input: RetargetingCandidateInput, timestamp: string): RetargetingCandidate {
  const base = {
    schemaVersion: SCHEMA_VERSION,
    timestamp,
    displayName: input.displayName,
    imageUrl: input.imageUrl,
    targetUrl: input.targetUrl,
  } as const;

  if (input.useCase === RESLINK) {
    return {
      ...base,
      useCase: RESLINK,
      marshaCode: toMarshaCodeArray(input.marshaCode),
      ...(input.groupName !== undefined && { groupName: input.groupName }),
      ...(input.eventExpirationDate !== undefined && { eventExpirationDate: input.eventExpirationDate }),
      ...(input.cancellationFlag !== undefined && { cancellationFlag: input.cancellationFlag }),
    };
  }

  if (input.useCase === RLM) {
    return {
      ...base,
      useCase: RLM,
      ...(input.marshaCode !== undefined && { marshaCode: input.marshaCode }),
    };
  }

  return {
    ...base,
    useCase: HWS,
    ...(input.marshaCode !== undefined && { marshaCode: input.marshaCode }),
  };
}

function mergeCandidate(
  existing: RetargetingCandidate[],
  next: RetargetingCandidate
): { candidates: RetargetingCandidate[]; nextIndex: number } {
  const candidates = existing.slice();

  const duplicateIndex = findDuplicateIndex(candidates, next);
  if (duplicateIndex !== -1) {
    candidates[duplicateIndex] = next;
    return { candidates, nextIndex: duplicateIndex };
  }

  if (candidates.length >= MAX_ENTRIES) {
    const evictIndex = pickEvictionIndex(candidates);
    if (evictIndex !== -1) candidates.splice(evictIndex, 1);
  }

  candidates.push(next);
  return { candidates, nextIndex: candidates.length - 1 };
}

function writeWithQuotaRetry(candidates: RetargetingCandidate[], nextIndex: number): boolean {
  try {
    writeRetargetingCandidateStore(candidates);
    return true;
  } catch (error) {
    if (!isQuotaExceededError(error)) return false;
  }

  // On retry, never evict the just-recorded candidate — the caller asked us to record it,
  // and returning `true` with that candidate missing from the store would be silent data loss.
  // We exclude `nextIndex` from the eviction pool so the selector can never pick it.
  const pool = candidates.filter((_, index) => index !== nextIndex);
  const evictIndexInPool = pickEvictionIndex(pool);
  if (evictIndexInPool === -1) return false;

  // Translate the pool-relative index back to a candidates-array index, skipping nextIndex.
  const evictIndexInCandidates = evictIndexInPool < nextIndex ? evictIndexInPool : evictIndexInPool + 1;
  const trimmed = candidates.slice();
  trimmed.splice(evictIndexInCandidates, 1);

  try {
    writeRetargetingCandidateStore(trimmed);
    return true;
  } catch {
    return false;
  }
}

export function recordRetargetingCandidate(
  input: RetargetingCandidateInput,
  options?: RecordRetargetingCandidateOptions
): boolean {
  try {
    if (!hasLocalStorage() || !isValidInput(input)) {
      return false;
    }

    // WEB-207389: consent gate — never write unless the visitor may be retargeted.
    if (!canExecuteHomepageRetargeting()) {
      return false;
    }

    const timestamp = currentTimestamp(options?.now ?? (() => new Date()));
    if (timestamp === null) return false;

    const next = buildCandidate(input, timestamp);
    const { candidates, nextIndex } = mergeCandidate(readRetargetingCandidateStore(), next);

    return writeWithQuotaRetry(candidates, nextIndex);
  } catch {
    return false;
  }
}
